import React, { useRef, useState, useEffect } from "react";
import { TrendingUp, Users, Building2, FileText, Hammer, BarChart3, Star, Shield, Zap, Home, Layers, Award } from "lucide-react";

const iconMap = { TrendingUp, Users, Building2, FileText, Hammer, BarChart3, Star, Shield, Zap, Home, Layers, Award };

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const ServiceCard = ({ service, index }) => {
  const [ref, inView] = useInView(0.1);
  const Icon = iconMap[service.icon] || Star;

  return (
    <div
      ref={ref}
      data-testid={`service-card-${index}`}
      className={`service-card bg-white border border-[#173269]/10 p-8 rounded-sm group scroll-hidden ${inView ? "scroll-visible" : ""}`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      {/* Icon */}
      <div className="w-16 h-16 bg-[#173269] flex items-center justify-center mb-6 group-hover:bg-[#ecc775] transition-colors duration-300 rounded-sm">
        <Icon size={28} className="text-[#ecc775] group-hover:text-[#173269] transition-colors duration-300" />
      </div>

      {/* Content */}
      <h3 className="text-[#173269] font-bold text-xl mb-3">{service.title}</h3>
      <p className="text-[#4A5568] text-sm leading-relaxed">{service.description}</p>

      {/* Bottom accent */}
      <div className="mt-6 h-0.5 w-0 bg-[#ecc775] group-hover:w-full transition-all duration-500"></div>
    </div>
  );
};

const Services = ({ services }) => {
  const [titleRef, titleInView] = useInView(0.2);

  const defaultServices = [
    { id: "1", title: "التسويق العقاري", description: "نصل إلى أوسع شريحة من المستثمرين والمشترين عبر قنوات تسويقية متعددة", icon: "TrendingUp", order: 1 },
    { id: "2", title: "الوساطة العقارية", description: "نربط البائعين بالمشترين ونحقق أفضل الصفقات بشفافية كاملة", icon: "Users", order: 2 },
    { id: "3", title: "إدارة الأملاك", description: "ندير عقاراتكم بكفاءة لضمان أفضل عائد استثماري", icon: "Building2", order: 3 },
    { id: "4", title: "العقود الإيجارية", description: "نوثق عقودكم عبر منصة إيجار الرسمية بضمانات قانونية", icon: "FileText", order: 4 },
    { id: "5", title: "التطوير العقاري", description: "نطور المشاريع وفق أعلى معايير الجودة والابتكار", icon: "Hammer", order: 5 },
    { id: "6", title: "المزادات العقارية", description: "ننظم مزادات إلكترونية متخصصة بشفافية ومهنية", icon: "BarChart3", order: 6 },
  ];

  const displayServices = services?.length > 0 ? services : defaultServices;

  return (
    <section id="services" data-testid="services-section" className="py-24 bg-[#F9F8F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={titleRef} className={`text-center mb-16 scroll-hidden ${titleInView ? "scroll-visible" : ""}`}>
          <p className="text-[#ecc775] font-bold text-sm uppercase tracking-widest mb-3">خدماتنا المتكاملة</p>
          <h2 className="text-4xl md:text-5xl font-black text-[#173269] mb-4">ما نقدمه لك</h2>
          <div className="section-divider mx-auto mb-6"></div>
          <p className="text-[#4A5568] text-lg max-w-2xl mx-auto leading-relaxed">
            نوفر لكم منظومة متكاملة من الخدمات العقارية التي تلبي جميع احتياجاتكم في سوق العقارات السعودي
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayServices.map((service, i) => (
            <ServiceCard key={service.id || i} service={service} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
